/**
 * Admin Gamification API Contracts
 *
 * Response schemas for the admin gamification endpoint:
 *   GET /api/v1/admin/gamification/customers/:customerId
 *
 * Returns raw Prisma model data (uppercase enums) — distinct from the
 * customer-facing gamification contracts in ../gamification/index.ts
 * which use lowercase enum variants.
 *
 * Consumers:
 *   - gamification-service AdminGamificationController (producer)
 *   - ticketing-service AdminVisitorDetailService BFF (consumer)
 */

import { z } from 'zod';

// ============================================================================
// Enums (uppercase — as stored in DB and returned by admin endpoints)
// ============================================================================

export const AdminGamificationTierSchema = z.enum(['BRONZE', 'SILVER', 'GOLD', 'PLATINUM']);

export type AdminGamificationTier = z.infer<typeof AdminGamificationTierSchema>;

export const AdminPointsTransactionTypeSchema = z.enum(['EARN', 'SPEND', 'EXPIRE', 'ADJUST']);

export type AdminPointsTransactionType = z.infer<typeof AdminPointsTransactionTypeSchema>;

export const AdminPointsReasonSchema = z.enum([
  'TICKET_PURCHASE',
  'ATTRACTION_RIDE',
  'ACHIEVEMENT_UNLOCK',
  'CHALLENGE_COMPLETE',
  'REFERRAL',
  'BIRTHDAY_BONUS',
  'PROMO',
  'ADJUSTMENT',
  'REDEMPTION',
  'EXPIRY',
]);

export type AdminPointsReason = z.infer<typeof AdminPointsReasonSchema>;

export const AdminDisplayModeSchema = z.enum(['REAL_NAME', 'NICKNAME', 'ANONYMOUS']);

export type AdminDisplayMode = z.infer<typeof AdminDisplayModeSchema>;

// ============================================================================
// GamificationProfile (raw Prisma shape)
// ============================================================================

export const AdminGamificationProfileSchema = z.object({
  id: z.string(),
  customerId: z.string(),
  currentTier: AdminGamificationTierSchema,
  tierPoints: z.number().int().nonnegative(),
  currentBalance: z.number().int().nonnegative(),
  lifetimeEarned: z.number().int().nonnegative(),
  lifetimeSpent: z.number().int().nonnegative(),
  currentStreak: z.number().int().nonnegative(),
  longestStreak: z.number().int().nonnegative(),
  lastActivityAt: z.string().nullable(),
  tierCalculatedAt: z.string().nullable(),
  leaderboardDisplayName: z.string().nullable(),
  leaderboardOptIn: z.boolean(),
  displayMode: AdminDisplayModeSchema,
  friendsOnlyLeaderboard: z.boolean(),
  referralCode: z.string().nullable(),
  referralCount: z.number().int().nonnegative(),
  totalAttractionRides: z.number().int().nonnegative(),
  totalVisits: z.number().int().nonnegative(),
  dailyPointsEarned: z.number().int().nonnegative(),
  dailyPointsResetAt: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export type AdminGamificationProfile = z.infer<typeof AdminGamificationProfileSchema>;

// ============================================================================
// PointsTransaction (raw Prisma shape, last 10 returned by admin endpoint)
// ============================================================================

export const AdminPointsTransactionSchema = z.object({
  id: z.string(),
  profileId: z.string(),
  transactionType: AdminPointsTransactionTypeSchema,
  points: z.number().int(),
  previousBalance: z.number().int().nonnegative(),
  newBalance: z.number().int().nonnegative(),
  reason: AdminPointsReasonSchema,
  sourceType: z.string().nullable(),
  sourceId: z.string().nullable(),
  description: z.string().nullable(),
  multiplier: z.number(),
  expiresAt: z.string().nullable(),
  expiredAt: z.string().nullable(),
  createdAt: z.string(),
});

export type AdminPointsTransaction = z.infer<typeof AdminPointsTransactionSchema>;

// ============================================================================
// Full response from GET /api/v1/admin/gamification/customers/:customerId
// ============================================================================

export const AdminGamificationCustomerResponseSchema = z.object({
  profile: AdminGamificationProfileSchema,
  badgeCount: z.number().int().nonnegative(),
  challengeCount: z.number().int().nonnegative(),
  recentTransactions: z.array(AdminPointsTransactionSchema),
});

export type AdminGamificationCustomerResponse = z.infer<
  typeof AdminGamificationCustomerResponseSchema
>;
